import { useEffect, useCallback, useState, useRef } from "react";

import { usCharFromKeyEvent } from "../utils/usKeyboardMap";

interface BarcodeScannerOptions {
  prefix?: string;
  suffix1?: string;
  suffix2?: string;
  timeout?: number;
  interCharTimeout?: number;
  enabled?: boolean;
  onScan?: (barcode: string) => void;
  shouldCapture?: () => boolean;
  transform?: (barcode: string) => string;
}

interface BarcodeScannerResult {
  currentScan: string;
  clearScan: () => void;
  isEnabled: boolean;
  setEnabled: (enabled: boolean) => void;
}

/**
 * A React hook for capturing barcode scanner input with configurable prefix and suffixes.
 * The scan is processed automatically when the suffixes are detected, without requiring Enter.
 * Can be controlled externally and provides callbacks for scan events.
 *
 * Keystrokes are resolved through {@link usCharFromKeyEvent}, which reads the *physical* key
 * (`event.code`) rather than `event.key`. Our scanners are configured to US English (QWERTY)
 * — see [docs/barcode-scanners.md](../../../../docs/barcode-scanners.md) — so this is what
 * makes a scan decode identically on an AZERTY or QWERTZ host.
 *
 * Optimized to minimize re-renders by using refs for internal state that doesn't need to trigger renders.
 *
 * @param {BarcodeScannerOptions} options - Configuration options
 * @param {string} [options.prefix=''] - Prefix to expect before the barcode
 * @param {string} [options.suffix1=''] - First suffix to expect after the barcode
 * @param {string} [options.suffix2=''] - Second suffix to expect after the barcode
 * @param {number} [options.timeout=100] - Timeout in ms to consider input as complete
 * @param {number} [options.interCharTimeout=50] - Max ms between keystrokes for them to count as
 *   one scanner burst. A slower keystroke is human typing: it breaks the burst, clears the buffer
 *   and reaches the DOM untouched.
 * @param {boolean} [options.enabled=true] - Whether the scanner is currently enabled
 * @param {function} [options.onScan] - Callback when a barcode is scanned
 * @param {function} [options.shouldCapture] - Function to determine if keystrokes should be captured
 * @param {function} [options.transform] - Function to transform the scanned barcode before onScan is called
 * @returns {BarcodeScannerResult} Object containing the current scan, control functions, and state
 */
const useBarcodeScanner = ({
  prefix = "",
  suffix1 = "",
  suffix2 = "",
  timeout = 100,
  interCharTimeout = 50,
  enabled = true,
  onScan,
  shouldCapture,
  transform,
}: BarcodeScannerOptions = {}): BarcodeScannerResult => {
  // Only use state for values that need to trigger re-renders
  const [currentScan, setCurrentScan] = useState<string>("");
  const [isEnabled, setIsEnabled] = useState<boolean>(enabled);

  // Use refs for internal state that doesn't need to trigger renders
  const bufferRef = useRef<string>("");
  const lastKeyTimeRef = useRef<number>(0);
  const isScanningRef = useRef<boolean>(false);
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const optionsRef = useRef({
    prefix,
    suffix1,
    suffix2,
    timeout,
    interCharTimeout,
    onScan,
    shouldCapture,
    transform,
  });

  // Update options ref when props change
  useEffect(() => {
    optionsRef.current = {
      prefix,
      suffix1,
      suffix2,
      timeout,
      interCharTimeout,
      onScan,
      shouldCapture,
      transform,
    };
  }, [prefix, suffix1, suffix2, timeout, interCharTimeout, onScan, shouldCapture, transform]);

  /** Drop any in-flight burst without touching the last completed scan. */
  const resetBuffer = useCallback(() => {
    bufferRef.current = "";
    isScanningRef.current = false;
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = null;
    }
  }, []);

  const clearScan = useCallback(() => {
    setCurrentScan("");
    resetBuffer();
  }, [resetBuffer]);

  const setEnabled = useCallback(
    (enabled: boolean) => {
      setIsEnabled(enabled);
      if (!enabled) {
        clearScan();
      }
    },
    [clearScan],
  );

  const processScan = useCallback((scan: string) => {
    const { prefix, suffix1, suffix2, onScan, transform } = optionsRef.current;
    const hasSuffix1 = suffix1 !== "";
    const hasSuffix2 = suffix2 !== "";
    const combinedSuffix = hasSuffix1 && hasSuffix2 ? suffix1 + suffix2 : "";

    if (
      scan.startsWith(prefix) &&
      ((hasSuffix1 && hasSuffix2 && scan.endsWith(combinedSuffix)) ||
        (hasSuffix1 && !hasSuffix2 && scan.endsWith(suffix1)) ||
        (!hasSuffix1 && hasSuffix2 && scan.endsWith(suffix2)))
    ) {
      // Remove prefix and suffixes to get the actual barcode
      let barcode = scan;
      if (prefix) barcode = barcode.slice(prefix.length);
      if (hasSuffix1 && hasSuffix2) {
        barcode = barcode.slice(0, -(suffix1.length + suffix2.length));
      } else if (hasSuffix1) {
        barcode = barcode.slice(0, -suffix1.length);
      } else if (hasSuffix2) {
        barcode = barcode.slice(0, -suffix2.length);
      }

      // Apply transform function if provided
      const transformedBarcode = transform ? transform(barcode) : barcode;

      setCurrentScan(transformedBarcode);
      bufferRef.current = "";

      // Call the onScan callback if provided
      if (onScan) {
        onScan(transformedBarcode);
      }
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isEnabled) return;

      const { timeout, interCharTimeout, suffix1, suffix2, prefix, shouldCapture } =
        optionsRef.current;

      // Check if we should capture this keystroke
      if (shouldCapture && !shouldCapture()) return;

      // Escape clears on every layout — it carries no character.
      if (event.key === "Escape") {
        resetBuffer();
        return;
      }

      // Resolve the *physical* key to the US character the scanner meant to send.
      // `null` means the event can't be scan input (modifier, shortcut, IME, AltGr,
      // auto-repeat, non-printable): leave the buffer and timers alone.
      const char = usCharFromKeyEvent(event);
      if (char === null) return;

      const currentTime = Date.now();
      const sinceLastKey = currentTime - lastKeyTimeRef.current;
      lastKeyTimeRef.current = currentTime;

      // A gap longer than `timeout` ends any burst. A gap longer than `interCharTimeout`
      // mid-buffer means a human is typing, not a scanner — drop the burst so this
      // keystroke starts fresh and is not swallowed below.
      if (sinceLastKey > timeout || (bufferRef.current !== "" && sinceLastKey > interCharTimeout)) {
        resetBuffer();
      }

      const newBuffer = bufferRef.current + char;
      bufferRef.current = newBuffer;

      // Check if we're starting a barcode scan (has prefix)
      if (prefix && newBuffer.startsWith(prefix)) {
        isScanningRef.current = true;
      }

      // If we're in the middle of a scan, prevent default to stop input field from receiving keystrokes
      if (isScanningRef.current) {
        event.preventDefault();
        event.stopPropagation();
      }

      // Check if we have a complete scan with suffixes
      if (suffix1 || suffix2) {
        processScan(newBuffer);
        isScanningRef.current = false;
      } else if (isScanningRef.current) {
        // If we're scanning but don't have suffixes, use timeout to detect completion
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
        }
        scanTimeoutRef.current = setTimeout(() => {
          if (isScanningRef.current && bufferRef.current.startsWith(prefix)) {
            processScan(bufferRef.current);
            isScanningRef.current = false;
          }
        }, timeout);
      }
    },
    [isEnabled, processScan, resetBuffer],
  );

  useEffect(() => {
    if (!isEnabled) return undefined;

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown, isEnabled]);

  // Update enabled state when prop changes
  useEffect(() => {
    setIsEnabled(enabled);
  }, [enabled]);

  return {
    currentScan,
    clearScan,
    isEnabled,
    setEnabled,
  };
};

export default useBarcodeScanner;
